/**
 * Calls to the hub's auth module (apps/hub/src/ext/auth). In demo mode (?mock=1) they run
 * against in-memory data so every screen works without a hub.
 */
import type {
  AuthProviders,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateJoinRuleRequest,
  CreateLinkInviteRequest,
  CreateTokenRequest,
  CreateTokenResponse,
  InvitePeek,
  JoinRule,
  Me,
  MembersResponse,
  MyIdentities,
  OidcProviderId,
  PolicyPreview,
  Role,
  SignInPolicyRequest,
  SignInProvider,
  SignInProviderId,
  SignInSettings,
  StartFlowResponse,
  TokensResponse,
} from '@orgus/protocol';
import { SIGN_IN_PROVIDERS, SIGN_IN_PROVIDER_LABEL, isSignInProvider } from '@orgus/protocol';
import { ApiError, api, apiDelete, apiGet, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import * as mock from './mockAuth.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const later = <T,>(v: () => T, ms = 250): Promise<T> => new Promise((resolve, reject) => setTimeout(() => {
  try {
    resolve(v());
  } catch (e) {
    reject(e);
  }
}, ms));

/** Leaves the page for the identity provider (or the right origin). Replaceable in tests. */
export type Navigate = (url: string) => void;
const assign: Navigate = (url) => window.location.assign(url);

/**
 * The sign-in buttons a hub offers, in order. Newer hubs send `list`; older hubs and the
 * control plane only say whether GitHub is configured.
 */
export function providerList(p: AuthProviders | null | undefined): SignInProvider[] {
  if (!p) return [];
  if (Array.isArray(p.list)) return p.list.filter((x) => isSignInProvider(x?.id) && x.startUrl === `/api/auth/${x.id}/start`);
  return p.github?.configured ? [{ id: 'github', label: SIGN_IN_PROVIDER_LABEL.github, startUrl: '/api/auth/github/start' }] : [];
}

/** A same-origin path to come back to after signing in, or null when `next` is not one. */
export function safeNextPath(next: string | null | undefined): string | null {
  if (!next || next.length > 512) return null;
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return null;
  if (/[\u0000-\u001f\u007f\\]/.test(next)) return null;
  return next;
}

/**
 * The address of the Sign in with <provider> button: a same-origin GET with a safe `next`.
 * Plain sign-in only: an invite never goes in a URL (use authClient.startInvite).
 */
export function signInUrl(id: SignInProviderId, o: { next?: string | null } = {}): string {
  const base = `/api/auth/${id}/start`;
  const next = safeNextPath(o.next);
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

/**
 * The buttons the sign-in screen shows, GitHub, Google, Microsoft in that order, whatever
 * order the hub listed them in. Empty when the hub could not say (an older hub): the screen
 * then offers the owner link alone.
 */
export function signInButtons(p: AuthProviders | null | undefined): SignInProvider[] {
  const rank = (id: SignInProviderId) => SIGN_IN_PROVIDERS.indexOf(id);
  const seen = new Set<SignInProviderId>();
  return providerList(p)
    .filter((x) => !seen.has(x.id) && (seen.add(x.id), true))
    .sort((a, b) => rank(a.id) - rank(b.id));
}

/**
 * Where a Sign in with <provider> button goes: the start address the hub listed, plus a safe
 * same-origin `next` so the browser comes back to the page it was on.
 */
export function providerStartUrl(p: Pick<SignInProvider, 'startUrl'>, next?: string | null): string {
  const back = safeNextPath(next);
  return back ? `${p.startUrl}?next=${encodeURIComponent(back)}` : p.startUrl;
}

/** The http(s) origin of `v`, or null. */
export function safeOrigin(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.origin : null;
  } catch {
    return null;
  }
}

/** The origin the hub says an invite or link has to be opened at (409 wrong-origin), or null. */
export function wrongOriginTarget(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const ui = e.ui as { code?: unknown; openAt?: unknown };
  return ui.code === 'wrong-origin' ? safeOrigin(ui.openAt) : null;
}

/** The hub wants a fresh sign-in first (403 reauth: linking or confirming from a session older than 10 minutes). */
export function needsFreshSignIn(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && (e.ui as { code?: unknown }).code === 'reauth';
}

/** Follow a StartFlowResponse only to an absolute http(s) address. */
function follow(r: StartFlowResponse, go: Navigate): void {
  const u = new URL(r.url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  if (u.protocol !== 'http:' && u.protocol !== 'https:') throw new ApiError({ what: 'The sign-in did not start.', why: 'The hub answered with an address the page will not open.', next: 'Reload the page and try again.' });
  go(u.toString());
}

const enc = encodeURIComponent;

export const authClient = {
  providers: (): Promise<AuthProviders> => (isMock() ? later(mock.mockProviders, 0) : apiGet('/api/auth/providers')),
  // Demo &signin-state=loading: the session check never answers, so the splash stays up.
  me: (): Promise<Me> => (isMock() ? (mock.mockSignInState() === 'loading' ? new Promise<Me>(() => {}) : later(mock.mockMe, 0)) : apiGet('/api/me')),
  members: (): Promise<MembersResponse> => (isMock() ? later(mock.mockMembers) : apiGet('/api/auth/members')),
  tokens: (): Promise<TokensResponse> => (isMock() ? later(mock.mockTokens) : apiGet('/api/auth/tokens')),

  setRule: (kind: 'user' | 'org', name: string, role: Role): Promise<unknown> =>
    isMock() ? later(() => mock.mockSetRule(kind, name, role)) : apiPost('/api/auth/rules', { kind, name, role }),
  removeRule: (kind: 'user' | 'org', name: string): Promise<unknown> =>
    isMock() ? later(() => mock.mockRemoveRule(kind, name)) : apiDelete(`/api/auth/rules/${kind}/${enc(name)}`),
  setRole: (memberId: string, role: Role): Promise<unknown> =>
    isMock() ? later(() => mock.mockSetRole(memberId, role)) : api(`/api/auth/members/${enc(memberId)}`, { method: 'PATCH', body: { role } }),
  removeMember: (memberId: string): Promise<{ tokensRevoked: number; stillAllowedVia?: string }> =>
    isMock() ? later(() => mock.mockRemoveMember(memberId)) : apiDelete(`/api/auth/members/${enc(memberId)}`),

  createToken: (req: CreateTokenRequest): Promise<CreateTokenResponse> => (isMock() ? later(() => mock.mockCreateToken(req), 400) : apiPost('/api/auth/tokens', req)),
  revokeToken: (id: string): Promise<unknown> => (isMock() ? later(() => mock.mockRevokeToken(id)) : apiDelete(`/api/auth/tokens/${enc(id)}`)),

  /**
   * Sign in with GitHub, Google or Microsoft: leaves this page for the hub's start address,
   * which sends the browser on to the provider and back to `next`. In demo mode it signs in
   * after a short pause instead.
   */
  async startSignIn(p: Pick<SignInProvider, 'startUrl'>, next?: string | null, go: Navigate = assign): Promise<void> {
    if (isMock()) return later(mock.mockSignIn, 500);
    go(providerStartUrl(p, next));
  },

  // ---------------------------------------------------------------- Google and Microsoft

  /** Providers, join rules, invites, identities and removed people (admins see everything; others see themselves). */
  signInSettings: (): Promise<SignInSettings> => (isMock() ? later(mock.mockSignInSettings) : apiGet('/api/auth/sign-in')),
  /** Owner only. With dryRun, nothing changes: the answer says how many people would be signed out. */
  setSignInPolicy: (body: SignInPolicyRequest, o: { dryRun?: boolean } = {}): Promise<PolicyPreview> =>
    isMock() ? later(() => mock.mockSetPolicy(body, !!o.dryRun)) : api(`/api/auth/sign-in/policy${o.dryRun ? '?dryRun=1' : ''}`, { method: 'PUT', body }),
  addJoinRule: (rule: CreateJoinRuleRequest): Promise<JoinRule> => (isMock() ? later(() => mock.mockAddJoinRule(rule)) : apiPost('/api/auth/join-rules', rule)),
  removeJoinRule: (id: string): Promise<unknown> => (isMock() ? later(() => mock.mockRemoveJoinRule(id)) : apiDelete(`/api/auth/join-rules/${enc(id)}`)),

  createInvite: (req: CreateInviteRequest): Promise<CreateInviteResponse> => (isMock() ? later(() => mock.mockCreateInvite(req), 400) : apiPost('/api/auth/invites', req)),
  createLinkInvite: (req: CreateLinkInviteRequest): Promise<CreateInviteResponse> =>
    isMock() ? later(() => mock.mockCreateLinkInvite(req), 400) : apiPost('/api/auth/invites/link', req),
  revokeInvite: (id: string): Promise<unknown> => (isMock() ? later(() => mock.mockRevokeInvite(id)) : apiDelete(`/api/auth/invites/${enc(id)}`)),
  /** What an invite would do, without using it. No sign-in needed. */
  peekInvite: (code: string): Promise<InvitePeek> => (isMock() ? later(() => mock.mockPeekInvite(code)) : apiPost('/api/auth/invites/peek', { code })),

  /**
   * Redeem an invite with a provider: the hub answers with the provider's address and this
   * page goes there. When the invite has to be opened at another origin (409 wrong-origin),
   * the page re-opens the invite there instead; the secret stays in the fragment.
   */
  async startInvite(provider: OidcProviderId, code: string, next?: string | null, go: Navigate = assign): Promise<void> {
    if (isMock()) return later(() => mock.mockStartInvite(provider, code));
    try {
      follow(await apiPost<StartFlowResponse>(`/api/auth/${provider}/start`, { invite: code, next: safeNextPath(next) ?? '/' }), go);
    } catch (e) {
      const origin = wrongOriginTarget(e);
      if (!origin) throw e;
      go(`${origin}/#invite=${enc(code)}`);
    }
  },

  myIdentities: (): Promise<MyIdentities> => (isMock() ? later(mock.mockMyIdentities) : apiGet('/api/auth/identities/mine')),
  /**
   * Link a Google or Microsoft account to whoever is signed in: leaves for the provider and
   * comes back to `next`. The hub refuses (403 `reauth`) when this browser signed in more
   * than 10 minutes ago (LINK_RECENT_MS): sign in again, then link.
   */
  async linkIdentity(provider: OidcProviderId, next = '/settings/sign-in', go: Navigate = assign): Promise<void> {
    if (isMock()) return later(() => mock.mockLinkIdentity(provider), 500);
    follow(await apiPost<StartFlowResponse>('/api/auth/identities/link', { provider, next: safeNextPath(next) ?? '/settings/sign-in' }), go);
  },
  unlinkIdentity: (id: string): Promise<{ ok: true; sessionsEnded: number }> =>
    isMock() ? later(() => mock.mockUnlinkIdentity(id)) : apiDelete(`/api/auth/identities/${enc(id)}`),
  /**
   * Confirm a sign-in an admin added to your account through an invite, so it no longer
   * depends on that admin. Needs a browser sign-in from the last 10 minutes that did not come
   * through that account (403 `reauth` or `forbidden` otherwise, with what to do next).
   */
  confirmIdentity: (id: string): Promise<{ ok: true }> => (isMock() ? later(() => mock.mockConfirmIdentity(id)) : apiPost(`/api/auth/identities/${enc(id)}/confirm`)),
  /** Sign out every browser signed in as you, this one included. */
  signOutEverywhere: (): Promise<{ ok: true; sessionsEnded: number }> => (isMock() ? later(mock.mockSignOutEverywhere) : apiPost('/api/auth/sessions/end-mine')),
  allowRemoved: (id: string): Promise<unknown> => (isMock() ? later(() => mock.mockAllowRemoved(id)) : apiPost(`/api/auth/removed/${enc(id)}/allow`)),

  // ---------------------------------------------------------------- session

  /** Ends the member session and the owner-link session, whichever this browser has. */
  async signOut(): Promise<void> {
    if (isMock()) return later(() => undefined, 150);
    await apiPost('/api/auth/sign-out').catch(() => undefined);
    await apiDelete('/api/session').catch(() => undefined);
  },

  /** Exchange an owner link (or just its token) for a session cookie. */
  async signInWithOwnerLink(text: string): Promise<void> {
    const m = /(?:^|[#&])token=([^&\s]+)/.exec(text.trim());
    const token = decodeURIComponent(m?.[1] ?? text.trim());
    if (isMock()) return later(() => undefined);
    await apiPost('/api/session', { token });
  },
};
